import * as React from "react";
import { FC, useRef } from "react";
import classNames from "classnames";
import Draggable from "react-draggable";
import Portal from "./Portal";
import "./Popup.scss";

export interface IPopupProps {
    id: string;
    isMobile: boolean;
    hasBackdrop?: boolean;
    className?: string;
    defaultPosition?: { x: number; y: number };
    notDraggable?: boolean;
    sameContainer?: boolean;
    hasShadow?: boolean;
    onDrag?: () => void;
}

export const Popup: FC<IPopupProps> = (props) => {
    const popupRef = useRef<HTMLDivElement>();

    const mapHeight = document.getElementById("geolite-map").clientHeight;
    const newHeight = mapHeight - 150 < popupRef?.current?.clientHeight && mapHeight - 75;
    newHeight && (popupRef.current.style.maxHeight = newHeight + "px");

    return props.isMobile ? (
        <div id={props.id} className="popup">
            <div className={classNames("container", { shadow: props.hasShadow })}>{props.children}</div>
        </div>
    ) : props.sameContainer ? (
        <div className="popup" id={props.id}>
            {props.hasBackdrop && <div className={"popup-backdrop-relative"}></div>}
            <div className={"popup-container " + props.className}>
                <div className={classNames("popup-content", { shadow: props.hasShadow })} ref={popupRef}>
                    {props.children}
                </div>
            </div>
        </div>
    ) : (
        <Portal id={props.id}>
            {props.hasBackdrop && <div className={"popup-backdrop"}></div>}
            <div className="popup">
                <div className={"popup-container " + props.className}>
                    {props.notDraggable ? (
                        <div className={classNames("popup-content", { shadow: props.hasShadow })} ref={popupRef}>
                            {props.children}
                        </div>
                    ) : (
                        <Draggable
                            axis="both"
                            handle=".popup-header"
                            defaultPosition={props.defaultPosition || { x: 0, y: 0 }}
                            onDrag={props.onDrag}
                        >
                            <div>
                                <div
                                    className={classNames("popup-content", { shadow: props.hasShadow })}
                                    ref={popupRef}
                                >
                                    {props.children}
                                </div>
                            </div>
                        </Draggable>
                    )}
                </div>
            </div>
        </Portal>
    );
};

export const PopupHeader: FC<{ isMobile: boolean; children?: React.ReactNode }> = (props) => {
    return props.isMobile ? (
        <div className="header">{props.children}</div>
    ) : (
        <div className="popup-header">
            <div className="popup-header-container">{props.children}</div>
        </div>
    );
};

export const PopupBody: FC<{ isMobile: boolean; children?: React.ReactNode }> = (props) => {
    return props.isMobile ? (
        <div className="content">{props.children}</div>
    ) : (
        <div className="popup-body">
            <div className="body-container">{props.children}</div>
        </div>
    );
};

export const PopupFooter: FC<{
    isMobile: boolean;
    onClick?: ({ position, contentWidth }: { position: number[]; contentWidth: number }) => void;
    children?: React.ReactNode;
}> = (props) => {
    const footerRef = useRef<HTMLDivElement>();

    const onClick = (e) => {
        if (!footerRef.current) {
            return;
        }

        const element = footerRef.current.getBoundingClientRect();
        props.onClick({
            position: [element.x + element.width / 2, element.y],
            contentWidth: e.target.firstChild.clientWidth
        });
    };

    return props.isMobile ? (
        <div className="footer" onClick={onClick} ref={footerRef}>
            {props.children}
        </div>
    ) : (
        <div className="popup-footer" draggable={true} onClick={onClick} ref={footerRef}>
            <div className="popup-footer-container">{props.children}</div>
        </div>
    );
};
